import { toast } from "sonner";

import { sheetManager } from "@/shared/components/sheet";

import type { ServiceHandler } from "../bridge/message-router";
import { getPluginDrawerSheetKey } from "../lib/plugin-panel-key";
import { usePluginStore } from "../state/plugin-store";

/**
 * UI 服务：将插件发起的界面操作桥接到宿主页面。
 *
 * 负责全局提示（toast）、原生确认/提示框、带安全属性的外链、基于 Blob 的下载，
 * 以及通过插件 store 打开或关闭插件面板；部分与 iframe 尺寸/标题相关的 RPC 在宿主 iframe 层处理。
 */
export const uiServiceHandler: ServiceHandler = async (pluginId, method, params) => {
  switch (method) {
    case "showToast": {
      /* 在宿主页面展示短时 toast，类型决定样式，可选 duration 控制自动消失时间 */
      const { message, type = "info", duration } = params as {
        message: string;
        type?: "success" | "error" | "info" | "warning";
        duration?: number;
      };
      const opts = duration ? { duration } : undefined;
      switch (type) {
        /* 成功 / 错误 / 警告 / 默认信息：映射到 sonner 对应 API，可选自定义停留时长 */
        case "success": toast.success(message, opts); break;
        case "error": toast.error(message, opts); break;
        case "warning": toast.warning(message, opts); break;
        default: toast.info(message, opts); break;
      }
      return;
    }

    case "showConfirm": {
      const { title, description } = params as {
        title: string;
        description?: string;
      };
      /* 阻塞式确认：合并标题与描述后交给浏览器 confirm，返回值回传给插件 */
      return window.confirm(description ? `${title}\n${description}` : title);
    }

    case "showAlert": {
      const { title, description } = params as {
        title: string;
        description?: string;
      };
      /* 仅展示信息，无用户选择结果 */
      window.alert(description ? `${title}\n${description}` : title);
      return;
    }

    case "openUrl": {
      const { url } = params as { url: string };
      /* 新标签页打开，限制 opener 与引用来源以降低风险 */
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    case "download": {
      /* 将字符串内容封装为 Blob，通过临时 <a download> 触发浏览器保存文件 */
      const { content, filename, mimeType = "text/plain" } = params as {
        content: string;
        filename: string;
        mimeType?: string;
      };
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return;
    }

    case "openPanel": {
      const { path, display = "modal", width, height } = params as {
        path: string;
        display?: "popup" | "modal" | "drawer";
        width?: number;
        height?: number;
      };
      /* 请求宿主按路由路径展示插件 UI（弹层 / 模态 / 抽屉），并传入当前插件 id */
      usePluginStore.getState().requestOpenPanel({ pluginId, path, display, width, height });
      return;
    }

    case "close": {
      /* 关闭当前由插件面板占用的宿主 UI */
      sheetManager.close(getPluginDrawerSheetKey(pluginId));
      usePluginStore.getState().closePanel();
      return;
    }

    case "setTitle":
    case "sizeTo":
      /* 窗口标题与 iframe 尺寸由宿主 iframe 组件通过 postMessage 处理，此处不落逻辑 */
      return;

    default:
      throw Object.assign(new Error(`Unknown ui method: ${method}`), { code: "UNKNOWN_METHOD" });
  }
};
